import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { EASE } from "@/components/Reveal";

const HERO_IMG =
  "https://images.pexels.com/photos/16472162/pexels-photo-16472162.jpeg?auto=compress&cs=tinysrgb&w=2000";

const LINES = ["Towels, peshtemals", "and bathrobes —", "woven to be kept."];

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <motion.img
          src={HERO_IMG}
          alt="Traditional weaving loom in Denizli"
          data-testid="hero-background-image"
          className="w-full h-[120%] object-cover"
          initial={{ scale: 1.12, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/85 via-[#121212]/35 to-[#121212]/25" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pb-16 pt-40">
        <motion.p
          data-testid="hero-kicker"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="text-cream/70 text-[11px] sm:text-xs uppercase tracking-[0.35em] font-semibold mb-8"
        >
          Nevatextile — Denizli, Türkiye · Towel · Peshtemal · Bathrobe
        </motion.p>

        <h1 className="font-serif font-light text-cream leading-[0.98] tracking-tight text-[13vw] sm:text-7xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                data-testid={`hero-line-${i}`}
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.45 + i * 0.14, ease: EASE }}
              >
                {i === 2 ? (
                  <>
                    woven <em className="italic text-clay">to be kept.</em>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="text-cream/80 text-base sm:text-lg leading-relaxed max-w-md"
          >
            A family-run manufacturer from the weaving capital of Türkiye,
            producing private-label home textiles for hotels, spas and brands
            worldwide.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="flex items-center gap-4"
          >
            <button
              data-testid="hero-explore-button"
              onClick={() => onNavigate("collection")}
              className="group flex items-center gap-3 bg-cream text-ink px-7 py-4 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-clay hover:text-cream transition-colors duration-300"
            >
              The Collection
              <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-enquire-button"
              onClick={() => onNavigate("contact")}
              className="group flex items-center gap-3 border border-cream/40 text-cream px-7 py-4 text-[11px] uppercase tracking-[0.25em] font-semibold hover:border-cream hover:bg-cream/10 transition-colors duration-300"
            >
              Enquire
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
